import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import {
  CambioConfig,
  ConfigSeguimiento,
  SeguimientoService,
} from 'src/app/shared/services/seguimiento.service';

/** Un campo del modal, con lo que hay que saber antes de moverlo. */
interface Campo {
  clave: string;
  etiqueta: string;
  ayuda: string;
  tipo: 'numero' | 'fecha' | 'opciones' | 'texto';
  paso?: number;
  min?: number;
  max?: number;
  opciones?: string[];
  valor: string | number;
}

/**
 * Configuración del seguimiento.
 *
 * **No hay interruptor de encendido**: el módulo corre siempre. Lo que se
 * configura acá es cómo mide, no si mide.
 *
 * Cada campo lleva escrito qué mueve. No es didáctica: bajar el umbral verde de
 * 80% a 70% cambia el color de la cartera entera, y quien lo mueve tiene que
 * saberlo antes de apretar guardar. Las semanas ya cerradas no cambian —cada
 * una guardó los umbrales con los que se pintó—, así que esto afecta a la
 * semana en curso y a las que vengan.
 */
@Component({
  selector: 'app-seguimiento-config-modal',
  templateUrl: './config-modal.component.html',
  styleUrls: ['./config-modal.component.sass'],
})
export class ConfigModalComponent {
  guardando = false;
  aplicados: CambioConfig[] = [];

  grupos: Array<{ titulo: string; nota?: string; campos: Campo[] }> = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public datos: { config: ConfigSeguimiento | null },
    private ref: MatDialogRef<ConfigModalComponent>,
    private api: SeguimientoService,
  ) {
    this.armar(datos.config);
  }

  private armar(c: ConfigSeguimiento | null): void {
    if (!c) return;

    this.grupos = [
      {
        titulo: 'El semáforo',
        nota: 'Mueve el color de toda la cartera. Las semanas cerradas no cambian.',
        campos: [
          {
            clave: 'SEGUIMIENTO_VENTANA_DIAS',
            etiqueta: 'Días que mira el color',
            ayuda:
              'Días corridos terminando hoy. En 28 el lunes ya tiene 28 días de datos, así que el tablero no amanece en rojo; en 0 vuelve a medir la semana de calendario transcurrida, como antes del 15/09. Medido contra producción: 28 días da 19 alertas por semana con 54% de precisión, y 7 días da 44 con 39%. La columna «Semana» no cambia — sigue siendo el total de lunes a domingo.',
            tipo: 'numero',
            paso: 7,
            min: 0,
            max: 90,
            valor: c.ventanaDias,
          },
          {
            clave: 'SEGUIMIENTO_SILENCIO_FACTOR',
            etiqueta: 'Marcar callado a las',
            ayuda:
              'Veces la cadencia propia del cliente. En 2 se marca al que lleva el doble de su p90 de huecos sin aparecer: 13 avisos por semana, 56% de precisión, y el 31% de ellos no está en rojo. En 0 se apaga la columna. No es un plazo en días a propósito: el p90 va de 3 días en el cuartil bajo de la cartera a 25 en el decil alto.',
            tipo: 'numero',
            paso: 0.5,
            min: 0,
            max: 10,
            valor: c.silencioFactor,
          },
          {
            clave: 'SEGUIMIENTO_UMBRAL_VERDE',
            etiqueta: 'Verde desde',
            ayuda: 'Fracción del nivel normal. 0,80 = opera al 80% o más de lo suyo.',
            tipo: 'numero',
            paso: 0.05,
            min: 0.05,
            max: 5,
            valor: c.umbralVerde,
          },
          {
            clave: 'SEGUIMIENTO_UMBRAL_AMARILLO',
            etiqueta: 'Amarillo desde',
            ayuda: 'Por debajo de esto es rojo. Tiene que ser menor que el verde.',
            tipo: 'numero',
            paso: 0.05,
            min: 0.01,
            max: 5,
            valor: c.umbralAmarillo,
          },
          {
            clave: 'SEGUIMIENTO_SEMAFORO_MODO',
            etiqueta: 'Qué se compara',
            ayuda:
              'PEOR toma el peor de monto y frecuencia: ve al cliente que mantiene el ticket y viene menos veces, que es el que se está llevando el flujo. Sube la cantidad de alertas.',
            tipo: 'opciones',
            opciones: ['PEOR', 'USDT', 'OPS'],
            valor: c.semaforoModo,
          },
          {
            clave: 'SEGUIMIENTO_DIAS_MES',
            etiqueta: 'Días del mes',
            ayuda: 'Con esto se prorratea el nivel mensual a los días de la semana.',
            tipo: 'numero',
            paso: 0.1,
            min: 28,
            max: 31,
            valor: c.diasMes,
          },
        ],
      },
      {
        titulo: 'Los segmentos',
        nota: 'Se recalculan en el cierre de mes junto con el nivel normal.',
        campos: [
          {
            clave: 'SEGUIMIENTO_TIER_WHALE_USDT',
            etiqueta: 'Whale desde (USDT/mes)',
            ayuda: 'Referencia del análisis: los 26 whales arrancan en 89.000.',
            tipo: 'numero',
            paso: 1000,
            min: 0,
            valor: c.tierWhaleUsdt,
          },
          {
            clave: 'SEGUIMIENTO_TIER_MEDIO_USDT',
            etiqueta: 'Medio desde (USDT/mes)',
            ayuda: 'Debajo de esto es cola: se revisa por excepción, no uno por uno.',
            tipo: 'numero',
            paso: 1000,
            min: 0,
            valor: c.tierMedioUsdt,
          },
        ],
      },
      {
        titulo: 'El nivel normal',
        nota: 'La referencia es una ventana cerrada: enero–agosto de 2026, donde está la pérdida.',
        campos: [
          {
            clave: 'SEGUIMIENTO_BASE_MESES',
            etiqueta: 'Meses de la ventana móvil',
            ayuda:
              'Promedio de los últimos N meses CON actividad. Más meses = base más estable y reacción más lenta.',
            tipo: 'numero',
            paso: 1,
            min: 1,
            max: 12,
            valor: c.baseMeses,
          },
          {
            clave: 'SEGUIMIENTO_HISTORIA_DESDE',
            etiqueta: 'Historia desde',
            ayuda: 'De acá sale el universo del módulo y cuánto valía cada cliente entero.',
            tipo: 'fecha',
            valor: c.historiaDesde,
          },
          {
            clave: 'SEGUIMIENTO_HISTORIA_HASTA',
            etiqueta: 'Historia hasta',
            ayuda: 'Cierra la ventana de referencia. Moverla cambia lo que hay que recuperar.',
            tipo: 'fecha',
            valor: c.historiaHasta,
          },
          {
            clave: 'SEGUIMIENTO_FUGA_DIAS',
            etiqueta: 'Días para pasar a reconquista',
            ayuda:
              'Sin operar tantos días, el cliente deja de ser alerta de la semana y pasa a la lista de reconquista. No sale del módulo.',
            tipo: 'numero',
            paso: 5,
            min: 7,
            valor: c.fugaDias,
          },
        ],
      },
      {
        titulo: 'Los indicadores del equipo',
        nota: 'Se congelan al cerrar cada semana: cambiar una meta no reescribe el pasado.',
        campos: [
          {
            clave: 'SEGUIMIENTO_META_COBERTURA_PCT',
            etiqueta: 'Meta de cobertura (%)',
            ayuda: 'Alertas con dueño Y próxima acción.',
            tipo: 'numero',
            paso: 5,
            min: 0,
            max: 100,
            valor: c.metaCoberturaPct,
          },
          {
            clave: 'SEGUIMIENTO_META_REACCION_DIAS',
            etiqueta: 'Meta de reacción (días)',
            ayuda: 'Desde que un whale se pone rojo hasta el primer contacto.',
            tipo: 'numero',
            paso: 1,
            min: 0,
            valor: c.metaReaccionDias,
          },
          {
            clave: 'SEGUIMIENTO_META_RECUPERACION_PCT',
            etiqueta: 'Meta de recuperación (%)',
            ayuda: 'Cuentas en alerta que vuelven a verde dentro de la ventana.',
            tipo: 'numero',
            paso: 5,
            min: 0,
            max: 100,
            valor: c.metaRecuperacionPct,
          },
          {
            clave: 'SEGUIMIENTO_RECUPERACION_DIAS',
            etiqueta: 'Ventana de recuperación (días)',
            ayuda: 'La presentación pide 30. No es «contra la semana anterior».',
            tipo: 'numero',
            paso: 5,
            min: 7,
            valor: c.recuperacionDias,
          },
          {
            clave: 'SEGUIMIENTO_META_USDT_MES',
            etiqueta: 'Meta de volumen recuperado (USDT/mes)',
            ayuda: 'La referencia del programa es 150.000.',
            tipo: 'numero',
            paso: 10000,
            min: 0,
            valor: c.metaUsdtMes,
          },
        ],
      },
      {
        titulo: 'La tasa preferente',
        nota:
          'En cero, ninguna oferta está autorizada y el módulo no la deja registrar. Es la aprobación que Dirección tiene que dar para que el equipo pueda cerrar.',
        campos: [
          {
            clave: 'SEGUIMIENTO_TASA_PREFERENTE_MAX_PCT',
            etiqueta: 'Techo autorizado (puntos)',
            ayuda: 'Puntos de descuento sobre la tasa pública que operaciones puede ofrecer.',
            tipo: 'numero',
            paso: 0.1,
            min: 0,
            max: 99,
            valor: c.tasaPreferenteMaxPct,
          },
          {
            clave: 'SEGUIMIENTO_TASA_PREFERENTE_DIAS',
            etiqueta: 'Vigencia por defecto (días)',
            ayuda: 'Una tasa preferente sin vencimiento es un descuento para siempre.',
            tipo: 'numero',
            paso: 5,
            min: 1,
            valor: c.tasaPreferenteDias,
          },
        ],
      },
      {
        titulo: 'Ritmo y avisos',
        campos: [
          {
            clave: 'SEGUIMIENTO_REFRESCO_MINUTOS',
            etiqueta: 'Refresco de la semana en curso (min)',
            ayuda:
              'Cada cuánto se recalcula la foto en vivo. En cero recalcula en cada visita: sirve para depurar, no para la reunión del lunes.',
            tipo: 'numero',
            paso: 1,
            min: 0,
            max: 60,
            valor: c.refrescoMinutos,
          },
          {
            clave: 'SEGUIMIENTO_RECORDATORIO_HORAS',
            etiqueta: 'Recordatorio único (horas)',
            ayuda: 'El guion dice uno solo, a las 48 h.',
            tipo: 'numero',
            paso: 1,
            min: 1,
            valor: c.recordatorioHoras,
          },
          {
            clave: 'SEGUIMIENTO_CADENCIA_MEDIO_SEMANAS',
            etiqueta: 'Cadencia de los medios (semanas)',
            ayuda: 'Los whales se revisan todas las semanas; los medios, cada tantas.',
            tipo: 'numero',
            paso: 1,
            min: 1,
            valor: c.cadenciaMedioSemanas,
          },
          {
            clave: 'SEGUIMIENTO_TELEGRAM_CHAT_ID',
            etiqueta: 'Chat de Telegram para avisos',
            ayuda: 'Vacío = no avisa. El canal en vivo sirve con el tablero abierto; el lunes a las 8 no lo está.',
            tipo: 'texto',
            valor: c.telegramChatId,
          },
        ],
      },
    ];
  }

  async guardar(): Promise<void> {
    if (this.guardando) return;
    this.guardando = true;
    this.aplicados = [];
    try {
      const cambios: Record<string, string | number> = {};
      for (const g of this.grupos) {
        for (const c of g.campos) cambios[c.clave] = c.valor;
      }
      const r = await this.api.guardarConfig(cambios);
      this.aplicados = r.aplicados;

      if (!r.aplicados.length) {
        await Swal.fire({
          title: 'Sin cambios',
          text: 'No se movió ningún parámetro.',
          icon: 'info',
          confirmButtonColor: '#2F4A2B',
        });
        this.ref.close(r.config);
        return;
      }

      await Swal.fire({
        title: 'Configuración guardada',
        html:
          '<div style="text-align:left;font-size:.9rem">' +
          r.aplicados
            .map(
              (a) =>
                `<div><b>${a.clave.replace('SEGUIMIENTO_', '')}</b>: ` +
                `${a.antes ?? '(vacío)'} → ${a.ahora}</div>`,
            )
            .join('') +
          '</div>',
        icon: 'success',
        confirmButtonColor: '#2F4A2B',
      });
      this.ref.close(r.config);
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo guardar',
        text: e?.error?.message ?? 'Revisá los valores.',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.guardando = false;
    }
  }

  cerrar(): void {
    this.ref.close();
  }
}
